export const FLIPBOOK_TEXT_MAX_LENGTH = 240
export const FLIPBOOK_TEXT_HANDLE_SIZE = 44

export interface TextFrame {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export interface TextPageSize {
  readonly width: number
  readonly height: number
}

export type TextFrameGesture = 'MOVE' | 'RESIZE_LEFT' | 'RESIZE_RIGHT'

function bound(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value))
}

// All dimensions are layout pixels. A rendered (zoomed) DOMRect is only used
// to map pointer deltas, never to normalize scrollHeight.
export function getTextFrameLayout(frame: TextFrame, page: TextPageSize, contentHeight: number) {
  const height = Math.max(FLIPBOOK_TEXT_HANDLE_SIZE, Math.ceil(contentHeight)) / page.height
  const width = bound(frame.width, 0, 1)
  const displayedHeight = Math.min(1, height)
  const fitted = {
    x: bound(frame.x, 0, 1 - width),
    y: bound(frame.y, 0, 1 - displayedHeight),
    width,
    height: displayedHeight,
  }
  return {
    frame: fitted,
    fits: Number.isFinite(height) && page.width > 0 && page.height > 0 && height <= 1 && width > 0,
    recovered: Math.abs(frame.height - fitted.height) * page.height > 1
      || Math.abs(frame.y - fitted.y) * page.height > 1,
  }
}

export function getTextGestureFrame(initial: TextFrame, gesture: TextFrameGesture, deltaX: number, deltaY: number, pageWidth: number): TextFrame {
  if (gesture === 'MOVE') {
    return { ...initial, x: initial.x + deltaX, y: initial.y + deltaY }
  }
  // Two 44px targets must be usable without changing the font size.
  const minimumWidth = Math.min(1, FLIPBOOK_TEXT_HANDLE_SIZE * 2 / pageWidth)
  const right = initial.x + initial.width
  const width = bound(initial.width + (gesture === 'RESIZE_LEFT' ? -deltaX : deltaX), minimumWidth, 1)
  return { ...initial, width, x: gesture === 'RESIZE_LEFT' ? bound(right - width, 0, 1 - width) : bound(initial.x, 0, 1 - width) }
}

export function measureTextContent(textarea: HTMLTextAreaElement, page: HTMLElement, width: number, text = textarea.value) {
  const wrapper = textarea.parentElement!
  const wrapperStyle = getComputedStyle(wrapper)
  const inputStyle = getComputedStyle(textarea)
  const px = (value: string) => Number.parseFloat(value) || 0
  const horizontalChrome = px(wrapperStyle.paddingLeft) + px(wrapperStyle.paddingRight)
    + px(wrapperStyle.borderLeftWidth) + px(wrapperStyle.borderRightWidth)
  const verticalChrome = px(wrapperStyle.paddingTop) + px(wrapperStyle.paddingBottom)
    + px(wrapperStyle.borderTopWidth) + px(wrapperStyle.borderBottomWidth)
  // A hidden clone avoids changing editor height/caret/scroll while measuring.
  const probe = textarea.cloneNode(false) as HTMLTextAreaElement
  probe.removeAttribute('id')
  probe.removeAttribute('aria-describedby')
  probe.setAttribute('aria-hidden', 'true')
  probe.tabIndex = -1
  probe.value = text
  Object.assign(probe.style, {
    position: 'absolute', visibility: 'hidden', pointerEvents: 'none',
    left: '0', top: '0', width: `${Math.max(1, page.clientWidth * width - horizontalChrome)}px`,
    height: '0', minHeight: '0', maxHeight: 'none', fieldSizing: 'fixed', overflow: 'hidden',
    font: inputStyle.font, letterSpacing: inputStyle.letterSpacing, scrollbarGutter: inputStyle.scrollbarGutter,
    padding: inputStyle.padding, boxSizing: 'border-box',
  })
  page.append(probe)
  try {
    return Math.max(probe.scrollHeight, px(inputStyle.minHeight)) + verticalChrome + 2
  } finally {
    probe.remove()
  }
}

export function acceptsTextInsertion(previous: string, insertion: string, start: number, end: number) {
  return previous.length - (end - start) + insertion.length <= FLIPBOOK_TEXT_MAX_LENGTH
}
